import { useEffect, useRef, useState, createContext, FC, RefObject, ReactNode, useCallback } from 'react';
import { AssistantSmartAppCommand, SendDataParams } from '@salutejs/client/dist/typings';
import {
  AssistantAppState,
  AssistantClientCommand,
  createAssistant,
  createSmartappDebugger
} from '@salutejs/client';

interface RunAppCommand extends AssistantSmartAppCommand {
  type: 'smart_app_data';
  smart_app_data: {
    type: 'run_app';
    payload: { accountId: string };
  };
}

type CustomAssistantCommand = AssistantClientCommand | RunAppCommand;

interface GeneratePhrase {
  image: string;
}

export interface AssistantContextValue {
  assistantRef: RefObject<ReturnType<typeof createAssistant>> | null;
  userId: string;
  accessToken: string;
  generatePhrase: null | GeneratePhrase;
  sendDataSafely: (data: SendDataParams) => void;
}

export const AssistantContext = createContext<AssistantContextValue>({
  assistantRef: null,
  userId: '',
  accessToken: '',
  generatePhrase: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  sendDataSafely: () => { }
});

const IS_DEVELOPMENT = import.meta.env.VITE_ENV === 'development';
const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN;
const DEV_PHRASE = import.meta.env.VITE_DEV_PHRASE;

console.log('VITE_SERVER_URL1', import.meta.env.VITE_SERVER_URL);

declare global {
  interface Window {
    AssistantHost?: {
      ready?: () => void;
    };
    appInitialData?: any[];
  }
}

const useAssistant = () => {
  const assistantRef = useRef<ReturnType<typeof createAssistant> | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [generatePhrase, setGeneratePhrase] = useState<GeneratePhrase | null>(null);
  const accountIdRef = useRef<string>('');

  const assistantState: { current: AssistantAppState } = {
    current: { item_selector: { items: [] } }
  };

  const getState = () => assistantState.current;

  const handleErrors = () => {
    throw new Error('Ошибка при выполнении запроса на получение токена');
  };

  const fetchToken = async (accountId: string) => {
    if (userId && accessToken) return;

    if (IS_DEVELOPMENT) {
      accountId = 'webdbg_userid_671hurtbn0tu177wpp8el9';
    }

    if (!accountId) {
      throw new Error('accountId is missing!');
    }
    accountIdRef.current = accountId;
    try {
      const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/user/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          saluteUserId: `${accountId}`
        })
      });

      const { jwt, userId } = await response.json();

      if (userId && jwt) {
        setUserId(userId);
        setAccessToken(jwt);
      } else {
        handleErrors();
      }
    } catch (error) {
      handleErrors();
    }
  };

  const processCommand = async (command: CustomAssistantCommand) => {
    const broadcast = new BroadcastChannel('generationActions');

    switch (command.type) {
      case 'smart_app_data':
        switch (command.smart_app_data.type) {
          case 'run_app':
            await fetchToken(command.smart_app_data.payload.accountId);
            break;
          case 'close_app':
            assistantRef.current?.close();
            break;
          case 'start_generate':
            // eslint-disable-next-line no-case-declarations
            const payload = command.smart_app_data.payload;
            // eslint-disable-next-line no-case-declarations
            const accountId = payload.accountId;
            if (accountId) {
              await fetchToken(accountId);
            }
            onStartGenerate(payload);
            break;
          case 'next_generate':
          case 'RIGHT':
            broadcast.postMessage({ action: 'next' });
            break;
          case 'previous_generate':
          case 'LEFT':
            broadcast.postMessage({ action: 'previous' });
            break;
          case 're_generate':
            broadcast.postMessage({ action: 'retry' });
            break;
          // TODO: отключение публикации
          // case 'publish':
          //   broadcast.postMessage({ action: 'publish' });
          //   break;
          case 'share':
            broadcast.postMessage({ action: 'share' });
            break;
          case 'openFavorites':
            broadcast.postMessage({ action: 'openFavorites' });
            break;
          case 'openGallery':
            broadcast.postMessage({ action: 'openGallery' });
            break;
          case 'openFeed':
            broadcast.postMessage({ action: 'openFeed' });
            break;
        }
    }
  };

  const assistantInit = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    let assistant: ReturnType<typeof createAssistant>;

    if (IS_DEVELOPMENT && DEV_TOKEN && DEV_PHRASE) {
      console.log('IS DEVELOPMENT - Using Smartapp Debugger');
      assistant = createSmartappDebugger({
        token: DEV_TOKEN,
        initPhrase: DEV_PHRASE,
        getState,
      });
    } else {
      assistant = createAssistant({ getState });
    }

    assistant.on('data', (command: CustomAssistantCommand) => {
      try {
        processCommand(command);
      } catch (error) {
        console.error('Error processing command:', error);
      }
    });

    return assistant;
  };

  const onStartGenerate = (command: GeneratePhrase) => {
    if (command) setGeneratePhrase(command);
    setTimeout(() => {
      setGeneratePhrase(null);
    }, 1000);
  };

  const sendDataSafely = useCallback((data: SendDataParams) => {
    const assistant = assistantRef.current;
    if (assistant && typeof assistant.sendData === 'function') {
      try {
        assistant.sendData(data);
        return true;
      } catch (error) {
        console.error('Error sending data to assistant:', error);
        return false;
      }
    } else {
      console.warn('Assistant not ready for sending data');
      return false;
    }
  }, []);

  const earlyInit = () => {
    if (typeof window !== 'undefined' && Array.isArray(window.appInitialData)) {
      console.log('Processing early init data');
      for (const command of window.appInitialData) {
        try {
          processCommand(command as CustomAssistantCommand);
        } catch (error) {
          console.error('Error processing early init command:', error);
        }
      }
    }
  };

  useEffect(() => {
    const init = () => {
      try {
        if (typeof window === 'undefined') {
          return;
        }

        const assistant = assistantInit();
        
        if (assistant) {
          assistantRef.current = assistant;

          earlyInit();
        } else {
          console.error('Assistant initialization returned null');
        }

      } catch (error) {
        console.error('Assistant initialization error:', error);
      }
    };

    init();

    return () => {
      if (assistantRef.current && typeof assistantRef.current.close === 'function') {
        try {
          assistantRef.current.close();
        } catch (error) {
          console.error('Error closing assistant:', error);
        }
      }
    };
  }, []);

  return {
    assistantRef,
    userId,
    accessToken,
    generatePhrase,
    sendDataSafely,
  };
};

interface AssistantProviderProps {
  children: ReactNode;
}

const AssistantProvider: FC<AssistantProviderProps> = ({ children }) => {
  const assistantInstance = useAssistant();

  return (
    <AssistantContext.Provider value={assistantInstance}>{children}</AssistantContext.Provider>
  );
};

export { AssistantProvider };
